import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import FpModal from './FpModal';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import { FPs } from "../../../../types/FabricPatterns";
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));


const FabricPattern: React.FC = () => {
  const [fabricpatterndata, setFabricPatternData] = useState<FPs[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<FPs | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchFt = async () => {
    try {
      const data = await apiRequest<FPs[]>({
        method: 'get',
        url: 'fabric-patterns',
      });
      setFabricPatternData(data);
    } catch (error) {
      showError('Failed to fetch fabric pattern');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFt();
  }, []);

  const handleEditClick = (record: FPs) => {
    setIsEditMode(true);
    setInitialValues({
      fp_id: record.fp_id,
      fp_pattern: record.fp_pattern,
      fp_status: record.fp_status,
      fp_parent: record.fp_parent,
      fp_image: record.fp_image,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchFt();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (fp_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-fabric-pattern/${fp_id}`,
      });
      showSuccess('Fabric Pattern deleted');
      fetchFt();
    } catch {
      showError('Failed to delete fabric pattern');
    }
  };

  const handleStatusToggle = async (ftId: number) => {
    setFabricPatternData((prev) =>
      prev.map((item) =>
        item.fp_id === ftId
          ? { ...item, fp_status: item.fp_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-fabric-pattern/${ftId}`,
      });
      showSuccess('Fabric Pattern status updated');
    } catch (error) {
      showError('Failed to update status');
      setFabricPatternData((prev) =>
        prev.map((item) =>
          item.fp_id === ftId
            ? { ...item, fp_status: item.fp_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<FPs>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<FPs> = [
    {
      title: 'Fabric Group',
      dataIndex: 'fp_parent',
      key: 'fp_parent',
      width: 200,
      filters: Array.from(new Set(fabricpatterndata.map((s) => s.fp_parent))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.fp_parent || null,
      // onFilter: (value, record) => record.fp_parent.includes(value as string),
      // sorter: (a, b) => a.fp_parent.localeCompare(b.fp_parent),
      sortOrder: sortedInfo.columnKey === 'fp_parent' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.fp_parent}</div>,
    },
    {
      title: 'Fabric Pattern',
      dataIndex: 'fp_pattern',
      key: 'fp_pattern',
      width: 500,
      filters: Array.from(new Set(fabricpatterndata.map((s) => s.fp_pattern))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.fp_pattern || null,
      onFilter: (value, record) => record.fp_pattern.includes(value as string),
      sorter: (a, b) => a.fp_pattern.localeCompare(b.fp_pattern),
      sortOrder: sortedInfo.columnKey === 'fp_pattern' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.fp_pattern}</div>,
    },
    {
      title: 'Image',
      dataIndex: 'fp_image',
      key: 'fp_image',
      width: 500,
      render: (_: any, record: any) =>
        record ? (
          <img
            src={record.fp_image}
            alt="Fabric Pattern"
            style={{
              width: 40,
              height: 50,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        ) : (
          '-'
        ),

    }
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: FPs) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.fp_status}
                onChange={() => handleStatusToggle(record.fp_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this ft?"
              onConfirm={() => handleDelete(record.fp_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Fabric Pattern</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Fabric Pattern
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<FPs>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="fp_id"
          dataSource={fabricpatterndata.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No Fabric Pattern found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, fabricpatterndata.length)} of{' '}
                {fabricpatterndata.length}
              </span>
              <Pagination
                {...pagination}
                total={fabricpatterndata.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <FpModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        fabricpatterndata={fabricpatterndata}
      />
    </>
  );
};

export default FabricPattern;